import React, { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { router } from '@inertiajs/react'
import { useReturnTo } from '@/contexts/ReturnToContext'

interface Props {
  boardSlug: string
  taskId: number
}

export function AttachmentUploadForm({ boardSlug, taskId }: Props) {
  const returnTo = useReturnTo()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadCount, setUploadCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    if (selectedFiles.length === 0) return

    setUploading(true)
    setTotalCount(selectedFiles.length)
    setUploadCount(0)

    let completed = 0
    selectedFiles.forEach(file => {
      const formData = new FormData()
      formData.append('attachment[file]', file)
      if (returnTo) formData.append('return_to', returnTo)

      router.post(
        `/tasks/boards/${boardSlug}/tasks/${taskId}/attachments`,
        formData as any,
        {
          forceFormData: true,
          preserveScroll: true,
          onFinish: () => {
            completed++
            setUploadCount(completed)
            if (completed >= selectedFiles.length) {
              setUploading(false)
              setUploadCount(0)
              setTotalCount(0)
            }
          },
        }
      )
    })

    if (fileRef.current) fileRef.current.value = ''
  }

  return (
    <Box>
      <input
        ref={fileRef}
        type="file"
        multiple
        hidden
        onChange={handleFiles}
      />
      {uploading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <LinearProgress variant="determinate" value={totalCount > 0 ? (uploadCount / totalCount) * 100 : 0} />
          <Typography variant="caption" color="text.secondary">
            Uploading {uploadCount}/{totalCount}...
          </Typography>
        </Box>
      ) : (
        <Button
          variant="outlined"
          size="small"
          fullWidth
          startIcon={<UploadFileIcon />}
          onClick={() => fileRef.current?.click()}
        >
          Upload files
        </Button>
      )}
    </Box>
  )
}
